import { Device } from "@fairfleet/geotab";
import { flatten } from "lodash";
import { useEffect, useState } from "react";
import combineFirmwareData from "../utils/combineFirmwareData";
import executeCmdOnDevices from "../utils/executeCmdOnDevices";
import mergeObjects from "../utils/mergeObjects";
import { useGeotabClient } from "@/components/GeotabClientContext";
import { usePresetContext } from "@/components/PresetProvider";
import { useAddressesByDeviceStatusInfo } from "@/hooks/addresses/useAddresses";
import { useIoxAddOnByDeviceIds } from "@/hooks/ioxAddOn/useIoxAddOnByDeviceIds";
import getDiagnosticIdsFromPreset from "@/hooks/presets/getDiagnosticsFromPreset";
import { useStatusInfosByIds } from "@/hooks/statusInfos/useStatusInfosByIds";
import { useFeedTextMessage } from "@/hooks/textMessage/useFeedTextMessage";
import { useZonesByIds } from "@/hooks/zone/useZonesByIds";

export const useDeviceFirmwareData = (devices: Device[]) => {
  const { selectedPreset } = usePresetContext();
  const { geotabClient } = useGeotabClient();

  const deviceIds = devices.map(d => d.id);
  const { data: channelData = {} } = useIoxAddOnByDeviceIds(deviceIds);
  const { data: addedData } = useFeedTextMessage(deviceIds);
  const [feedData, setFeedData] = useState({});
  const { data: statusInfos = {} } = useStatusInfosByIds(
    deviceIds,
    getDiagnosticIdsFromPreset(selectedPreset),
  );

  const { data: addresses = {} } = useAddressesByDeviceStatusInfo(statusInfos, undefined, true);
  const zoneIds = flatten(Object.values(addresses).map(addr => addr.zones))
    .map(zone => (zone ? zone.id : "-1"))
    .filter(id => id != "-1");
  const { data: zones = {} } = useZonesByIds(zoneIds);

  // Incremently fetch getFeed textmessages
  useEffect(() => {
    setFeedData(addedData ? mergeObjects(feedData, addedData.data) : feedData);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addedData]);

  // Trigegr devices, to send textmessages
  useEffect(() => {
    if (devices.length > 0) {
      executeCmdOnDevices(geotabClient, deviceIds, "uiid", channelData);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deviceIds.length, channelData]);

  const data = combineFirmwareData(
    devices,
    channelData,
    feedData,
    statusInfos,
    addresses,
    zones ?? {},
  );

  return {
    data,
  };
};
